// src/pages/calendar/components/CalendarEventDetails.js
import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Title as TitleIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Flag as FlagIcon,
  Description as DescriptionIcon,
  Notifications as NotificationsIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import useUserRoles from "hooks/useUserRoles";
import ProcesosApi from "api/procesosApi";
import ConvocarAudiencia from "pages/procesos/resumen/components/convocarAudiencia";

const CalendarEventDetails = ({ isOpen, onClose, event = null, onEdit, onDelete }) => {
  const { user } = useUserRoles();
  const [modalConvocarOpen, setModalConvocarOpen] = useState(false);
  const [audienciaData, setAudienciaData] = useState(null);
  const [loadingAudiencia, setLoadingAudiencia] = useState(false);
  // Verificar si el evento tiene audiencia vinculada
  const tieneAudiencia = event?.extendedProps?.audiencia_id ? true : false;
  const audienciaId = event?.extendedProps?.audiencia_id;

  // Cargar datos de la audiencia cuando se abre el modal de convocar
  const handleConvocarClick = async () => {
    if (!audienciaId) return;
    setLoadingAudiencia(true);
    try {
      const response = await ProcesosApi.fetchAudienciaById(audienciaId);
      setAudienciaData(response.data);
      setModalConvocarOpen(true);
    } catch (error) {
      console.error("Error al cargar audiencia:", error);
      alert(obtenerMensajeError(error, "Error al cargar los datos de la audiencia"));
    } finally {
      setLoadingAudiencia(false);
    }
  };

  if (!event) return null;

  const getPriorityColor = (priority) => {
    const colors = {
      low: "success",
      medium: "warning",
      high: "error",
    };
    return colors[priority] || "warning";
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      low: "Baja",
      medium: "Media",
      high: "Alta",
    };
    return labels[priority] || "Media";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Pendiente",
      completed: "Completado",
      cancelled: "Cancelado",
      in_progress: "En progreso",
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      completed: "success",
      cancelled: "error",
      in_progress: "info",
    };
    return colors[status] || "default";
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString("es-CO", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("es-CO", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Verificar si el usuario puede editar
  // El usuario puede editar si:
  // 1. Es el usuario asignado (assignedTo es la identificación)
  // 2. Es el creador del evento (createdBy es la identificación)
  const assignedToId = event.extendedProps?.assignedTo?.identificacion;
  const createdById = event.extendedProps?.createdBy?.identificacion;
  const currentUserId = user?.identificacion;

  const canEdit =
    currentUserId && (assignedToId === currentUserId || createdById === currentUserId);

  // Función para obtener el nombre completo del usuario asignado
  const getAssignedToName = () => {
    const assignedTo = event.extendedProps?.assignedTo;
    if (typeof assignedTo === "object" && assignedTo !== null) {
      return assignedTo.nombre_completo || "No asignado";
    }
    return assignedTo || "No asignado";
  };

  // Función para obtener el nombre completo del creador
  const getCreatedByName = () => {
    const createdBy = event.extendedProps?.createdBy;
    if (typeof createdBy === "object" && createdBy !== null) {
      return createdBy.nombre_completo || "N/A";
    }
    return createdBy || "N/A";
  };

  const hcDeleteEvent = (id) => {
    const confirmAction = window.confirm(`¿Está seguro(a) de eliminar el Evento?`);
    if (!confirmAction) return;
    onDelete(id);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <SuiTypography variant="h4" fontWeight="medium">
            Detalle del Evento
          </SuiTypography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="left"
          flexWrap="wrap"
          gap={1}
          sx={{ mt: 2 }}
        >
          <SuiTypography variant="h5" fontWeight="medium">
            <TitleIcon fontSize="small" color="primary" />
            {event.title}
          </SuiTypography>
          <Box display="flex" gap={1}>
            <Chip
              icon={<FlagIcon />}
              label={getPriorityLabel(event.extendedProps?.priority)}
              color={getPriorityColor(event.extendedProps?.priority)}
              size="small"
            />
            {event.extendedProps?.status && (
              <Chip
                label={getStatusLabel(event.extendedProps?.status)}
                color={getStatusColor(event.extendedProps?.status)}
                size="small"
              />
            )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <SuiBox>
          <Grid container spacing={1}>
            {/* Ubicación y Asignado - Lado a lado */}
            <Grid size={{ xs: 6, md: 6 }}>
              <SuiBox
                p={1}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 2,
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  height: "100%",
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <PersonIcon color="primary" sx={{ mt: 0.5 }} />
                  <SuiBox flex={1}>
                    <SuiTypography variant="button" fontWeight="medium" mb={1} display="block">
                      Asignado a
                    </SuiTypography>
                    <SuiTypography variant="body2" textColor="text">
                      {getAssignedToName()}
                    </SuiTypography>
                  </SuiBox>
                </Box>
              </SuiBox>
            </Grid>

            {/* Ubicación */}
            <Grid size={{ xs: 6, md: 6 }}>
              <SuiBox
                p={1}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 2,
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  height: "100%",
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <LocationIcon color="primary" sx={{ mt: 0.5 }} />
                  <SuiBox flex={1}>
                    <SuiTypography variant="button" fontWeight="medium" mb={1} display="block">
                      Ubicación
                    </SuiTypography>
                    <SuiTypography variant="body2" textColor="text">
                      {event.extendedProps.location}
                    </SuiTypography>
                  </SuiBox>
                </Box>
              </SuiBox>
            </Grid>

            {/* Fecha y Hora */}
            <Grid size={12}>
              <SuiBox
                p={1}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 2,
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <AccessTimeIcon color="primary" sx={{ mt: 0.5 }} />
                  <SuiBox flex={1}>
                    <SuiTypography variant="button" fontWeight="medium" mb={1} display="block">
                      Fecha y Hora
                    </SuiTypography>
                    {event.allDay ? (
                      <SuiTypography variant="body2" textColor="text">
                        <strong>Todo el día:</strong> {formatDate(event.start)}
                      </SuiTypography>
                    ) : (
                      <>
                        <SuiTypography variant="body2" textColor="text" mb={0.5}>
                          <strong>Inicio:</strong> {formatDateTime(event.start)}
                        </SuiTypography>
                        <SuiTypography variant="body2" textColor="text">
                          <strong>Fin:</strong> {formatDateTime(event.end)}
                        </SuiTypography>
                      </>
                    )}
                  </SuiBox>
                </Box>
              </SuiBox>
            </Grid>

            {/* Descripción */}
            {event.extendedProps?.description && (
              <Grid size={12}>
                <SuiBox
                  p={1}
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                    borderRadius: 2,
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <DescriptionIcon color="primary" sx={{ mt: 0.5 }} />
                    <SuiBox flex={1}>
                      <SuiTypography variant="button" fontWeight="medium" mb={1} display="block">
                        Descripción
                      </SuiTypography>
                      <SuiTypography
                        variant="body2"
                        textColor="text"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {event.extendedProps.description}
                      </SuiTypography>
                    </SuiBox>
                  </Box>
                </SuiBox>
              </Grid>
            )}

            {/* Recordatorios */}
            {event.extendedProps?.reminders?.length > 0 && (
              <Grid size={12}>
                <SuiBox
                  p={1}
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                    borderRadius: 2,
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <NotificationsIcon color="primary" sx={{ mt: 0.5 }} />
                    <SuiBox flex={1}>
                      <SuiTypography variant="button" fontWeight="medium" mb={1} display="block">
                        Recordatorios
                      </SuiTypography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {event.extendedProps.reminders.map((reminder, index) => (
                          <Chip
                            key={index}
                            label={`${reminder} minutos antes`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </SuiBox>
                  </Box>
                </SuiBox>
              </Grid>
            )}

            {/* Información adicional */}
            {event.extendedProps?.createdBy && (
              <Grid size={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    pt: 2,
                    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <SuiTypography variant="caption" textColor="secondary">
                    Creado por: {getCreatedByName()}
                  </SuiTypography>
                  {event.extendedProps?.created_at && (
                    <SuiTypography variant="caption" textColor="secondary">
                      {new Date(event.extendedProps.created_at).toLocaleDateString("es-CO")}
                    </SuiTypography>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </SuiBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, mt: -2 }}>
        <SuiButton onClick={onClose} buttonColor="dark">
          Cerrar
        </SuiButton>
        <Box sx={{ flex: 1 }} />
        {tieneAudiencia && (
          <SuiButton
            onClick={handleConvocarClick}
            buttonColor="info"
            disabled={loadingAudiencia}
            sx={{ mr: 1 }}
          >
            {loadingAudiencia ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <SendIcon sx={{ mr: 0.5 }} fontSize="small" />
                Convocar
              </>
            )}
          </SuiButton>
        )}
        {tieneAudiencia && event?.start && dayjs(event.start).isAfter(dayjs(), "day") && (
          <SuiButton
            onClick={handleConvocarClick}
            buttonColor="info"
            disabled={loadingAudiencia}
            sx={{ mr: 1 }}
          >
            {loadingAudiencia ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <SendIcon sx={{ mr: 0.5 }} fontSize="small" />
                Convocar
              </>
            )}
          </SuiButton>
        )}
        {canEdit && (
          <>
            <SuiButton onClick={() => hcDeleteEvent(event.id)} buttonColor="light">
              Eliminar
            </SuiButton>
            <SuiButton onClick={onEdit} color="primary" buttonColor="dark">
              Editar
            </SuiButton>
          </>
        )}
      </DialogActions>

      {/* Modal de Convocatoria */}
      {tieneAudiencia && audienciaData && (
        <ConvocarAudiencia
          open={modalConvocarOpen}
          onClose={() => {
            setModalConvocarOpen(false);
            setAudienciaData(null);
          }}
          audiencia={audienciaData}
          proceso={audienciaData?.proceso_detalle || event?.extendedProps?.proceso}
          onSuccess={() => {
            setModalConvocarOpen(false);
            setAudienciaData(null);
          }}
        />
      )}
    </Dialog>
  );
};

CalendarEventDetails.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    start: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    end: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    allDay: PropTypes.bool,
    extendedProps: PropTypes.shape({
      description: PropTypes.string,
      location: PropTypes.string,
      priority: PropTypes.string,
      status: PropTypes.string,
      assignedTo: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          identificacion: PropTypes.string,
          nombre_completo: PropTypes.string,
        }),
      ]),
      createdBy: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          identificacion: PropTypes.string,
          nombre_completo: PropTypes.string,
        }),
      ]),
      reminders: PropTypes.arrayOf(PropTypes.number),
      created_at: PropTypes.string,
    }),
  }),
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default CalendarEventDetails;
